import { cn } from "@/lib/utils/cn";

import { Button } from "@/components/forms/Button";

import type { ColumnDef } from "@tanstack/react-table";
import type { LucideIcon } from "lucide-react";

export interface RowAction<TData> {
  /** Stable id, used as React key. */
  id: string;
  /** Lucide icon component. */
  icon: LucideIcon;
  /** Tooltip + aria-label. Receives the row so labels can be data-driven. */
  label: (row: TData) => string;
  /** Click handler. Receives the row; close over what you need. */
  onClick: (row: TData) => void;
  /** If true, this action is not rendered for this row. */
  hidden?: (row: TData) => boolean;
  /** If true, the button is rendered but disabled. */
  disabled?: (row: TData) => boolean;
  /** Tailwind classes for the icon. */
  iconClassName?: string;
  /** Tailwind classes for the button hover state. */
  buttonClassName?: string;
}

export interface RowActionsColumnConfig<TData> {
  actions: RowAction<TData>[];
  /** Disables all row actions when a bulk selection is active. */
  bulkSelectionActive?: boolean;
  /** Column header label. Defaults to "Actions". */
  header?: string;
  /** Column id. Defaults to "row-actions". */
  id?: string;
}

export function createRowActionsColumn<TData>(
  config: RowActionsColumnConfig<TData>,
): ColumnDef<TData> {
  const {
    actions,
    bulkSelectionActive = false,
    header = "Actions",
    id = "row-actions",
  } = config;

  return {
    id,
    header: function renderActionsHeader() {
      return <div className="flex-1 text-center">{header}</div>;
    },
    enableSorting: false,
    cell: function renderRowActionsCell({ row }) {
      const data = row.original;
      const visible = actions.filter(function isVisible(action) {
        return !action.hidden?.(data);
      });
      if (visible.length === 0) return null;

      return (
        // Always centered under the centered "Actions" header — the previous
        // single-action-only centering made multi-action rows sit left of it.
        <div className={cn("flex gap-1 justify-center")}>
          {visible.map(function renderActionButton(action) {
            const Icon = action.icon;
            const isDisabled =
              bulkSelectionActive || action.disabled?.(data) === true;
            const labelText = action.label(data);
            return (
              <Button
                key={action.id}
                iconOnly
                startIcon={
                  <Icon className={action.iconClassName ?? "w-4 h-4 text-gray-500"} />
                }
                variant="ghost"
                size="md"
                onClick={function handleActionClick(e) {
                  e.stopPropagation();
                  action.onClick(data);
                }}
                disabled={isDisabled}
                title={labelText}
                aria-label={labelText}
                className={action.buttonClassName ?? "border-transparent"}
              />
            );
          })}
        </div>
      );
    },
  };
}
